export interface Permission {
  id: string;
  name: string;
  description: string;
  category: PermissionCategory;
}

export type PermissionCategory =
  | 'users'
  | 'tenants'
  | 'reports'
  | 'files'
  | 'settings'
  | 'audit'
  | 'billing';

export interface Role {
  id: string;
  tenantId: string | null; // null for system roles
  name: string;
  description: string | null;
  permissions: string[]; // permission IDs
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRoleRequest {
  name: string;
  description?: string;
  permissions: string[];
}

export interface UpdateRoleRequest {
  name?: string;
  description?: string;
  permissions?: string[];
}

// Default permissions
export const PERMISSIONS = {
  // Users
  USERS_VIEW: 'users:view',
  USERS_CREATE: 'users:create',
  USERS_EDIT: 'users:edit',
  USERS_DELETE: 'users:delete',
  USERS_INVITE: 'users:invite',

  // Tenants (Super Admin only)
  TENANTS_VIEW: 'tenants:view',
  TENANTS_CREATE: 'tenants:create',
  TENANTS_EDIT: 'tenants:edit',
  TENANTS_DELETE: 'tenants:delete',

  // Reports
  REPORTS_VIEW: 'reports:view',
  REPORTS_CREATE: 'reports:create',
  REPORTS_EDIT: 'reports:edit',
  REPORTS_DELETE: 'reports:delete',
  REPORTS_EXPORT: 'reports:export',

  // Files
  FILES_VIEW: 'files:view',
  FILES_UPLOAD: 'files:upload',
  FILES_DOWNLOAD: 'files:download',
  FILES_DELETE: 'files:delete',

  // Knowledge Base
  KB_VIEW: 'kb:view',
  KB_MANAGE: 'kb:manage',

  // Settings
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_EDIT: 'settings:edit',
  SETTINGS_GLOBAL: 'settings:global',

  // Audit
  AUDIT_VIEW: 'audit:view',
  AUDIT_EXPORT: 'audit:export',

  // Billing
  BILLING_VIEW: 'billing:view',
  BILLING_MANAGE: 'billing:manage',

  // API Keys
  API_KEYS_VIEW: 'api_keys:view',
  API_KEYS_MANAGE: 'api_keys:manage',
} as const;

export type PermissionKey = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// Role permission presets
export const ROLE_PRESETS = {
  super_admin: Object.values(PERMISSIONS),
  tenant_admin: [
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_EDIT,
    PERMISSIONS.USERS_DELETE,
    PERMISSIONS.USERS_INVITE,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_CREATE,
    PERMISSIONS.REPORTS_EDIT,
    PERMISSIONS.REPORTS_DELETE,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.FILES_VIEW,
    PERMISSIONS.FILES_UPLOAD,
    PERMISSIONS.FILES_DOWNLOAD,
    PERMISSIONS.FILES_DELETE,
    PERMISSIONS.KB_VIEW,
    PERMISSIONS.KB_MANAGE,
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.SETTINGS_EDIT,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.BILLING_VIEW,
    PERMISSIONS.BILLING_MANAGE,
    PERMISSIONS.API_KEYS_VIEW,
    PERMISSIONS.API_KEYS_MANAGE,
  ],
  manager: [
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_CREATE,
    PERMISSIONS.REPORTS_EDIT,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.FILES_VIEW,
    PERMISSIONS.FILES_UPLOAD,
    PERMISSIONS.FILES_DOWNLOAD,
    PERMISSIONS.KB_VIEW,
    PERMISSIONS.SETTINGS_VIEW,
  ],
  user: [
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.FILES_VIEW,
    PERMISSIONS.FILES_UPLOAD,
    PERMISSIONS.FILES_DOWNLOAD,
    PERMISSIONS.SETTINGS_VIEW,
  ],
};
